import React from 'react';
import ReactDOM from 'react-dom';
import { BarChart3, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { api } from '@/lib/api';

export function LibraryVisualCard({ lv, onAdd, allFields, modelId, formatValue }) {
  const [hovered, setHovered] = React.useState(false);
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [popupPos, setPopupPos] = React.useState({ left: 0, top: 0 });
  const timerRef = React.useRef(null);
  const cardRef = React.useRef(null);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setPopupPos({ left: rect.right + 8, top: Math.min(rect.top, window.innerHeight - 240) });
      }
      setHovered(true);
      if (!previewData && !previewLoading && lv.model_id && (lv.row_dims?.length > 0 || lv.value_measures?.length > 0)) {
        setPreviewLoading(true);
        const dims = [...(lv.row_dims || []), ...(lv.col_dim ? [lv.col_dim] : [])];
        const topN = lv.top_n || lv.viz_config?.topN;
        const limit = topN?.enabled ? topN.n : 50;
        api.executeQuery({ model_id: lv.model_id, dimensions: dims, measures: lv.value_measures || [], limit }).then(res => {
          setPreviewData(res?.data || []);
        }).catch(() => setPreviewData([])).finally(() => setPreviewLoading(false));
      }
    }, 400);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setHovered(false);
  };

  return (
    <>
      <div ref={cardRef} className="p-2 rounded-lg border border-border/30 hover:border-primary/30 cursor-grab hover:bg-primary/5 transition-all"
        draggable onDragStart={e => e.dataTransfer.setData('text/plain', `library_visual:${lv.visual_id}`)}
        onDoubleClick={onAdd} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3 h-3 text-primary/50 flex-shrink-0" />
          <span className="text-[10px] font-medium truncate flex-1">{lv.name}</span>
          <Badge variant="outline" className="text-[7px] px-1 h-3.5 flex-shrink-0">{(lv.viz_type || '').replace('_', ' ')}</Badge>
        </div>
        {lv.description && <p className="text-[9px] text-muted-foreground/80 truncate mt-0.5 pl-4">{lv.description}</p>}
      </div>
      {hovered && ReactDOM.createPortal(
        <div className="fixed z-[200] w-72 bg-background border border-border/50 rounded-xl shadow-2xl overflow-hidden"
          style={{ left: popupPos.left, top: popupPos.top }}
          onMouseEnter={() => { clearTimeout(timerRef.current); setHovered(true); }} onMouseLeave={handleMouseLeave}>
          <div className="px-3 py-2 border-b border-border/30 bg-muted/20">
            <p className="text-[10px] font-medium truncate">{lv.name}</p>
            <p className="text-[9px] text-muted-foreground">{(lv.row_dims || []).length} dims, {(lv.value_measures || []).length} measures</p>
          </div>
          <div className="p-2" style={{ height: 180 }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : previewData && previewData.length > 0 ? (
              lv.viz_type === 'table' ? (
                <div className="text-[9px] overflow-auto h-full">
                  <table className="w-full"><tbody>
                    {previewData.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-b border-border/20">
                        {Object.values(row).slice(0, 3).map((v, j) => <td key={j} className="px-1 py-0.5 truncate max-w-[80px]">{formatValue(v)}</td>)}
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              ) : (
                <ChartRenderer vizType={lv.viz_type || 'bar_chart'} data={previewData.slice(0, 30)} rowDims={lv.row_dims || []} colDim={lv.col_dim || ''} showGrid={false} showLegend={false} vizConfig={{}} formatValue={formatValue} allFields={allFields} fieldOverrides={{}} onDrill={() => {}} marks={{}} />
              )
            ) : (
              <div className="flex items-center justify-center h-full text-[10px] text-muted-foreground/70">No preview available</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
